import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import gql from 'graphql-tag'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  StickyNote,
  Lightbulb,
  AlertTriangle,
  CheckSquare,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Lock,
} from 'lucide-react'
import { cn } from '@/lib/utils'

const AGENDA_ENTRY_HISTORY = gql`
  query agendaEntryHistory($lineageId: String!) {
    agendaEntryHistory(lineageId: $lineageId) {
      id
      source
      kind
      title
      status
      dueDate
      assignedToName
      occurrenceId
      occurrenceNumber
      createdAt
    }
  }
`

interface Props {
  lineageId: string
}

const KIND_ICONS: Record<string, any> = {
  NOTE: StickyNote,
  DECISION: Lightbulb,
  RISK: AlertTriangle,
  TASK: CheckSquare,
}

const KIND_TEXT: Record<string, string> = {
  NOTE: 'text-gray-600',
  DECISION: 'text-blue-600',
  RISK: 'text-red-600',
  TASK: 'text-amber-600',
}

const STATUS_ICONS: Record<string, any> = {
  OPEN: AlertCircle,
  IN_PROGRESS: Clock,
  RESOLVED: CheckCircle2,
  CLOSED: XCircle,
}

const STATUS_TEXT: Record<string, string> = {
  OPEN: 'text-gray-500',
  IN_PROGRESS: 'text-blue-600',
  RESOLVED: 'text-green-600',
  CLOSED: 'text-red-600',
}

const formatDateTime = (iso: string) => {
  const d = new Date(iso)
  return (
    d.toLocaleDateString() +
    ' ' +
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  )
}

const EntryHistoryPanel: React.FC<Props> = ({ lineageId }) => {
  const { t } = useTranslation()
  const { data, loading } = useQuery(AGENDA_ENTRY_HISTORY, {
    variables: { lineageId },
    skip: !lineageId,
    fetchPolicy: 'cache-and-network',
  })

  const versions = (data?.agendaEntryHistory || []) as any[]

  if (loading && versions.length === 0) {
    return (
      <div className="space-y-2 pt-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  if (versions.length === 0) {
    return (
      <div className="text-xs text-muted-foreground py-2">
        {t('SubItem_history_empty', {
          defaultValue: 'Tento záznam zatím nemá historii — toto je první verze.',
        })}
      </div>
    )
  }

  return (
    <ul className="space-y-2 pt-1">
      {versions.map((v) => {
        const KindIcon = KIND_ICONS[v.kind] || StickyNote
        const StatusIcon = STATUS_ICONS[v.status] || AlertCircle
        return (
          <li key={v.id} className="rounded-md border bg-muted/40 p-2 space-y-1">
            <div className="flex items-center gap-2 flex-wrap text-[11px]">
              {v.occurrenceNumber != null && (
                <Badge variant="outline" className="font-mono">
                  #{v.occurrenceNumber}
                </Badge>
              )}
              <span className="text-muted-foreground">{formatDateTime(v.createdAt)}</span>
              <Lock
                className="h-3 w-3 ml-auto text-muted-foreground"
                aria-label={t('SubItem_locked', { defaultValue: 'Zamknuto' })}
              />
            </div>
            <div className="flex items-start gap-2">
              <KindIcon className={cn('h-3.5 w-3.5 mt-0.5 shrink-0', KIND_TEXT[v.kind])} />
              <div className="flex-1 text-xs font-medium">{v.title}</div>
              <StatusIcon className={cn('h-3.5 w-3.5 mt-0.5 shrink-0', STATUS_TEXT[v.status])} />
            </div>
            <div className="text-[11px] text-muted-foreground pl-6 flex items-center gap-3 flex-wrap">
              {v.assignedToName && <span>👤 {v.assignedToName}</span>}
              {v.dueDate && <span>📅 {new Date(v.dueDate).toLocaleDateString()}</span>}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

export default EntryHistoryPanel
